// src/components/KnowledgeBase.tsx
//
// Sidebar panel for the personal knowledge base — lists documents the
// user has explicitly saved (via "Save to knowledge base" on an attached
// file), with a delete action. Mirrors ChatHistory.tsx's structure.
// Retrieval during chat happens server-side in chat_proxy.php; this
// component is purely for viewing/managing what's stored.
import React, { useEffect, useState, useCallback } from 'react';
import { kbList, kbDelete, KbDocument } from '../api';

interface Props {
  token: string;
  refreshTrigger: number;
}

export default function KnowledgeBase({ token, refreshTrigger }: Props) {
  const [docs, setDocs] = useState<KbDocument[]>([]);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    const currentToken = token || localStorage.getItem('coderai_token') || '';
    if (!currentToken) return;
    kbList(currentToken)
      .then(setDocs)
      .catch(err => setError(err.message || 'Could not load knowledge base'));
  }, [token]);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm('Remove this document from your knowledge base?')) return;
    const currentToken = token || localStorage.getItem('coderai_token') || '';
    try {
      await kbDelete(id, currentToken);
      load();
    } catch (err: any) {
      setError(err.message || 'Could not delete document');
    }
  };

  if (docs.length === 0 && !error) {
    return <p className="no-history">No documents saved yet</p>;
  }

  return (
    <div className="chat-history">
      {error && <p className="kb-error">{error}</p>}
      {docs.map(d => (
        <div key={d.id} className="history-item" title={`${d.chunk_count} chunk${d.chunk_count === 1 ? '' : 's'}`}>
          <span className="history-title">📚 {d.filename}</span>
          <button className="history-delete" onClick={e => handleDelete(e, d.id)}>🗑</button>
        </div>
      ))}
    </div>
  );
}